import * as React from 'react';
import * as ReactDOM from 'react-dom';

import ActionButton from './components/ActionButton';
import { CustomPropertyRow } from './CustomPropertyRow';
import { storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import SmallButton from './components/SmallButton';
import { styledWithProps } from '../utils';
import styled, { css } from 'styled-components';
import Tooltip from './components/ButtonTooltip';
import { MotionSignalSelect } from '../components/MotionSignalSelect';
import { SignalSource } from '../data/SignalSource';
import { Effect } from '@beatflyer/shaders-lib';
import { EditEffectCommand } from 'state/commands/EditEffectCommand';
import { ChangeEffectSignalCommand } from 'state/commands/ChangeEffectSignalCommand';
import { ToggleEffectMotionDirectionCommand } from 'state/commands/ToggleEffectMotionDirectionCommand';

interface InvertButtonProps {
  invert: boolean;
}
const InvertButton = styledWithProps<InvertButtonProps>()(styled(SmallButton))`
  background-color: var(--primary-text-color);
  mask: url('/media/icons/icon-effect-invert.svg') no-repeat 50% 50%;
  height: 20px;
  margin-right: -6px;
  margin-left: auto;

  ${props =>
    props.invert &&
    css`
      opacity: 1;
      background-color: var(--accent-color);
    `};
`;

interface Props {
  getTrigger: () => HTMLElement;
  effect: Effect;
  onClose: Function;
  onReset: () => void;
  onRemove: () => void;
}

interface State {
  properties: any;
}

export default class extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);

    const filter = props.effect;

    const properties = {};
    // console.log( filter );
    const ps = filter.getPropertiesDescriptors();
    for (var key in ps) {
      properties[key] = filter.getProperty(key);
    }
    this.state = {
      properties
    };
  }
  componentDidMount() {
    const trigger = this.props.getTrigger();
    if (trigger) {
      const box = trigger.getBoundingClientRect();
      const me = ReactDOM.findDOMNode(this) as HTMLElement;
      const top = Math.min(
        box.top - 4,
        window.innerHeight - me.getBoundingClientRect().height
      );
      me.style.left = box.right + 'px';
      me.style.top = top + 'px';
    }

    window.addEventListener('mousedown', this.didClickOnWindow);
  }

  componentWillUnmount() {
    window.removeEventListener('mousedown', this.didClickOnWindow);
  }

  didClickOnWindow = (e: MouseEvent) => {
    const cta = this.props.getTrigger();
    if (cta === e.target) {
      return;
    }
    const thisElement = ReactDOM.findDOMNode(this) as HTMLElement;
    const validContainers = [
      thisElement,
      document.querySelector('.chrome-picker'),
      document.querySelector('.react-tiny-popover-container')
    ];
    for (const el of validContainers) {
      if (el !== null && el.contains(e.target as HTMLElement)) {
        return;
      }
    }
    if ((e.target as HTMLElement).className === 'dim') {
      return;
    }
    this.props.onClose();
  };
  onCustomPropertyChange = (prop: string, value: any) => {
    const { effect } = this.props;
    if (effect.isPreviewing) {
      effect.setProperty(prop, value);
      storeDispatch(Actions.editedEffect(effect));
    } else {
      const initialValue = effect.getProperty(prop);
      storeDispatch(
        new EditEffectCommand(effect.id, prop, initialValue, value)
      );
    }
    const properties = this.state.properties;
    properties[prop] = value;
    this.setState({
      properties
    });
  };
  invertMotion = () => {
    storeDispatch(new ToggleEffectMotionDirectionCommand(this.props.effect.id));
  };

  didChangeSignal = (value: SignalSource) => {
    storeDispatch(new ChangeEffectSignalCommand(this.props.effect.id, value));
  };

  render() {
    const filter = this.props.effect;
    const customProperties: JSX.Element[] = [];
    const properties = filter.getPropertiesDescriptors();
    const createNode = (key: string) => {
      const p = properties[key];
      const label = p.label || key;
      let shouldBeVisible = true;
      if (p.shouldBeVisible) {
        shouldBeVisible = p.shouldBeVisible.call(this.props.effect);
      }
      if (shouldBeVisible) {
        customProperties.push(
          <CustomPropertyRow
            name={label}
            id={key}
            key={key}
            value={filter.getProperty(key)}
            data={p}
            onChange={this.onCustomPropertyChange}
          />
        );
      }
    };
    for (var k in properties) {
      createNode(k);
    }
    const isComposition =
      Object.getPrototypeOf(filter).constructor.id.startsWith('composition-');
    if (!filter.isSplitEffect && !isComposition) {
      customProperties.push(
        <div
          className="custom-property custom-property--motion"
          key="motion-signal"
        >
          <label>Animation</label>
          <MotionSignalSelect
            value={this.props.effect.signalSource}
            inverted={this.props.effect.isInverted}
            onChange={this.didChangeSignal}
          />
          <Tooltip text="Invert motion">
            <InvertButton
              className="invert-button"
              onClick={this.invertMotion}
              invert={this.props.effect.isInverted}
            />
          </Tooltip>
        </div>
      );
    }
    return (
      <div className="effects-settings-popover">
        <div className="popover-header">
          <span />
          {this.props.effect.isPreviewing ? (
            <ActionButton
              onClick={this.props.onReset}
              disabled={filter.hasInitialValues()}
              onBlack={true}
              icon="undo"
            >
              Reset
            </ActionButton>
          ) : (
            <ActionButton
              onClick={this.props.onRemove}
              danger={true}
              onBlack={true}
              icon="bin"
            >
              Remove
            </ActionButton>
          )}
        </div>
        <div className="effect-properties">{customProperties}</div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/EffectSettingsPopover.tsx